import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/research';

function getCsrfToken() {
    return Cookies.get('csrftoken') || '';
}

const authClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable sending cookies for session-based auth
});

authClient.interceptors.request.use(
    (config) => {
        const csrfToken = getCsrfToken();
        if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
            config.headers['X-CSRFToken'] = csrfToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let currentUser = null;

const setUser = (user) => {
    currentUser = user;
    if (user) {
        localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
        localStorage.removeItem('currentUser');
    }
};

export const Auth = {
    signUp: async ({ username, password, attributes = {} }) => {
        const response = await authClient.post('/auth/signup/', {
            email: username,
            password,
            firstName: attributes.given_name || '',
            lastName: attributes.family_name || '',
            attributes,
        });
        if (response.data.success && response.data.user) {
            setUser(response.data.user);
        }
        return response.data;
    },

    signIn: async (username, password) => {
        const response = await authClient.post('/auth/signin/', {
            email: username,
            password,
        });
        if (response.data.success && response.data.user) {
            setUser(response.data.user);
            try {
                window.dispatchEvent(new CustomEvent('app:auth:signin', { detail: { user: response.data.user } }));
            } catch (e) {}
        }
        return response.data;
    },

    signOut: async () => {
        try {
            await authClient.post('/auth/signout/');
        } catch (e) {
            // Error handled silently
        }
        setUser(null);
        localStorage.removeItem('currentUser');
    },

    currentSession: async () => {
        try {
            const response = await authClient.get('/auth/session/');
            if (response.data.success) {
                // Store a dummy token for compatibility with existing code that expects it
                const sessionData = {
                    username: response.data.username,
                    email: response.data.email,
                    is_staff: response.data.is_staff,
                    is_superuser: response.data.is_superuser,
                    contributor_access: response.data.contributor_access,
                    phone_number: response.data.phone_number,
                    given_name: response.data.fname,
                    family_name: response.data.lname,
                    institute: response.data.institute,
                    research_field: response.data.research_field,
                    research_geo: response.data.research_geo,
                    terms_version_acknowledged: response.data.terms_version_acknowledged,
                    privacy_version_acknowledged: response.data.privacy_version_acknowledged,
                    latest_terms_version: response.data.latest_terms_version,
                    latest_privacy_version: response.data.latest_privacy_version,
                };
                
                return {
                    getIdToken: () => ({
                        getJwtToken: () => 'session', // Dummy token for compatibility
                        payload: sessionData,
                    }),
                    getAccessToken: () => ({
                        getJwtToken: () => 'session', // Dummy token for compatibility
                        payload: {
                            id: response.data.id,
                            is_staff: response.data.is_staff,
                        },
                    }),
                    getIdToken: () => ({
                        getJwtToken: () => 'session', // Dummy token for compatibility
                        payload: {
                            id: response.data.id,
                            username: response.data.username,
                            email: response.data.email,
                            is_staff: response.data.is_staff,
                            is_superuser: response.data.is_superuser,
                            contributor_access: response.data.contributor_access,
                            phone_number: response.data.phone_number,
                            given_name: response.data.fname,
                            family_name: response.data.lname,
                            institute: response.data.institute,
                            research_field: response.data.research_field,
                            research_geo: response.data.research_geo,
                            terms_version_acknowledged: response.data.terms_version_acknowledged,
                            privacy_version_acknowledged: response.data.privacy_version_acknowledged,
                            latest_terms_version: response.data.latest_terms_version,
                            latest_privacy_version: response.data.latest_privacy_version,
                        },
                    }),
                };
            }
            throw new Error('Not authenticated');
        } catch (error) {
            throw new Error('No current user');
        }
    },

    currentAuthenticatedUser: async () => {
        try {
            const response = await authClient.get('/auth/user/');
            if (response.data.isAuthenticated) {
                setUser(response.data.user);
                return {
                    username: response.data.username,
                    attributes: {
                        email: response.data.email,
                    },
                };
            }
            throw new Error('Not authenticated');
        } catch (error) {
            throw new Error('No current user');
        }
    },

    currentCredentials: async () => {
        return {
            accessKeyId: '',
            secretAccessKey: '',
            sessionToken: '',
        };
    },

    changePassword: async (user, oldPassword, newPassword) => {
        const response = await authClient.post('/auth/change-password/', {
            oldPassword,
            newPassword,
        });
        return response.data;
    },

    forgotPassword: async (username) => {
        const response = await authClient.post('/auth/forgot-password/', {
            email: username,
        });
        return response.data;
    },

    forgotPasswordSubmit: async (username, code, newPassword) => {
        const response = await authClient.post('/auth/forgot-password-submit/', {
            email: username,
            code,
            newPassword,
        });
        return response.data;
    },

    confirmSignUp: async (username, code) => {
        const response = await authClient.post('/auth/confirm-signup/', {
            email: username,
            code,
        });
        return response.data;
    },

    resendSignUp: async (username) => {
        const response = await authClient.post('/auth/resend-signup/', {
            email: username,
        });
        return response.data;
    },

    updateUserAttributes: async (user, attributes) => {
        const response = await authClient.post('/auth/update-attributes/', {
            attributes,
        });
        return response.data;
    },

    verifyCurrentUserAttribute: async (attr) => {
        const response = await authClient.post('/auth/verify-email/', {});
        return response.data;
    },

    verifyCurrentUserAttributeSubmit: async (attr, code) => {
        const response = await authClient.post('/auth/verify-email/', { code });
        return response.data;
    },

    acknowledgeLegal: async () => {
        const response = await authClient.post('/auth/acknowledge-legal/', {});
        return response.data;
    },

    completeNewPassword: async (user, newPassword, requiredAttributes) => {
        return { success: true };
    },

    federatedSignIn: async (provider) => {
        console.warn('Federated sign in not implemented');
        return null;
    },
};

export default Auth;

